import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner, Table, Container } from 'react-bootstrap';
import api, { formatApiErrorDetail, getPublicUploadsOrigin } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import PortalProveedorPageHeader from '../components/PortalProveedorPageHeader';

const MisNotificaciones = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/v1/ordenes-notificadas/mine');
      setItems(res.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e, 'Error al cargar sus órdenes'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', '/mis-ordenes');
      navigate('/login');
      return;
    }
    load();
  }, [isAuthenticated]);

  const markRead = async (id) => {
    try {
      await api.patch(`/api/v1/ordenes-notificadas/${id}/leer`);
      setItems((prev) =>
        prev.map((x) =>
          x.id_notificacion === id
            ? { ...x, estado: 'LEIDA', fecha_lectura: new Date().toISOString() }
            : x
        )
      );
    } catch (e) {
      setError(formatApiErrorDetail(e, 'No se pudo marcar como leída'));
    }
  };

  const fileUrl = (item) => {
    const cod = (item.cod_convocatoria || item.id_convocatoria || '').toString().trim().replace(/\s+/g, '_');
    return `${getPublicUploadsOrigin()}/uploads/ordenes_notificadas/${cod}/${item.orden_pdf}`;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      <PortalProveedorPageHeader
        title="Mis órdenes"
        subtitle="Documentos PDF notificados por convocatoria. Marque como leída cuando los haya revisado."
      >
        <div className="d-flex flex-wrap gap-2 justify-content-md-end">
          <Button as={Link} to="/" variant="light" size="sm" className="shadow-sm">
            <i className="bi bi-house-door me-1" />
            Inicio
          </Button>
          <Button variant="primary" size="sm" onClick={load} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" />
            Actualizar
          </Button>
        </div>
      </PortalProveedorPageHeader>

      <Container className="py-4" style={{ maxWidth: 1200 }}>
        {error && <Alert variant="danger" className="border-0 shadow-sm">{error}</Alert>}

        <Card className="border-0 shadow-sm overflow-hidden">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted small mt-3 mb-0">Cargando sus órdenes…</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 portal-proveedor-table align-middle">
                  <thead>
                    <tr>
                      <th>Convocatoria</th>
                      <th>Enviado por</th>
                      <th>Estado</th>
                      <th>Fecha envío</th>
                      <th>Fecha lectura</th>
                      <th>Documento</th>
                      <th className="text-end">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          <i className="bi bi-inbox d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }} />
                          No tiene órdenes notificadas aún.
                        </td>
                      </tr>
                    ) : (
                      items.map((it) => (
                        <tr key={it.id_notificacion}>
                          <td>
                            <div className="fw-semibold text-dark">
                              {it.cod_convocatoria || it.id_convocatoria}
                            </div>
                            <small className="text-muted">{it.convocatoria_concepto || ''}</small>
                          </td>
                          <td className="small text-muted">
                            {it.notificador_nombre || it.id_usuario_notifica || '—'}
                          </td>
                          <td>
                            {it.estado === 'LEIDA' ? (
                              <Badge bg="success" className="rounded-pill">Leída</Badge>
                            ) : (
                              <Badge bg="warning" text="dark" className="rounded-pill">Pendiente</Badge>
                            )}
                          </td>
                          <td className="text-nowrap small">{new Date(it.fecha_envio).toLocaleString('es-PE')}</td>
                          <td className="text-nowrap small text-muted">
                            {it.fecha_lectura ? new Date(it.fecha_lectura).toLocaleString('es-PE') : '—'}
                          </td>
                          <td>
                            <a
                              href={fileUrl(it)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-decoration-none fw-medium"
                            >
                              <i className="bi bi-file-pdf me-1 text-danger" />
                              Ver PDF
                            </a>
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={it.estado === 'LEIDA'}
                              onClick={() => markRead(it.id_notificacion)}
                            >
                              Marcar leída
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </div>
  );
};

export default MisNotificaciones;
